'use client';

import { c as _c } from "react/compiler-runtime";
import type { IconButtonProps, MenuItemProps, DividerProps, PopperPlacementType } from '@mui/material';
import { Box, ClickAwayListener, Fade, Paper, Popper, Divider, IconButton, MenuItem, MenuList, Stack } from '@mui/material';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { GroupTitle } from './groupTitle';
import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';
type IsDisabledResult = boolean | {
  disabled: boolean;
  tooltip?: string;
};
export interface OptionMenuItemType {
  id: string;
  groupHeader?: boolean;
  divider?: boolean;
  href?: string;
  dividerProps?: DividerProps;
  icon?: ReactNode;
  text?: ReactNode;
  rightContent?: ReactNode;
  nonInteractive?: boolean;
  menuItemProps?: MenuItemProps;
  predicate?: () => boolean;
  isDisabled?: () => IsDisabledResult | Promise<IsDisabledResult>;
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  subMenu?: OptionType[];
  disabledTooltip?: string;
}
export type OptionType = string | OptionMenuItemType;
interface OptionsMenuType {
  icon?: ReactNode;
  iconClassName?: string;
  options: OptionType[];
  placement?: PopperPlacementType;
  iconButtonProps?: IconButtonProps;
  'data-testid'?: string;
  onToggle?: (open: boolean) => void;
  onOptionSelected?: (option: OptionType) => void;
  // Drop the open/close fade so the menu mounts and unmounts synchronously. Use
  // when selecting an option shifts the menu's anchor: with the fade, the
  // closing menu would slide to follow the moved anchor before disappearing.
  disableTransition?: boolean;
  Component?: React.ElementType;
  componentProps?: Record<string, unknown>;
  popperClassName?: string;
}
const InnerMenu = props => {
  const $ = _c(53);
  const {
    onOptionSelected,
    disableTransition,
    closeMenu,
    open,
    options,
    anchorEl,
    placement,
    popperClassName
  } = props;
  const [subMenuOpen, setSubMenuOpen] = useState();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [disabledStates, setDisabledStates] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [loadingIds, setLoadingIds] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== open || $[3] !== options) {
    t2 = () => {
      if (!open) {
        return;
      }
      const optionsWithDynamicDisabled = options.filter(_temp);
      if (optionsWithDynamicDisabled.length === 0) {
        return;
      }
      setLoadingIds(new Set(optionsWithDynamicDisabled.map(_temp2)));
      Promise.all(optionsWithDynamicDisabled.map(async option => {
        const raw = await option.isDisabled();
        const resolved = typeof raw === "boolean" ? {
          disabled: raw
        } : raw;
        setDisabledStates(prev => ({
          ...prev,
          [option.id]: resolved
        }));
        setLoadingIds(prev_0 => {
          const next = new Set(prev_0);
          next.delete(option.id);
          return next;
        });
      }));
    };
    t3 = [open, options];
    $[2] = open;
    $[3] = options;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== anchorEl || $[7] !== closeMenu) {
    t4 = event => {
      if (anchorEl?.contains(event.target as HTMLElement)) {
        return;
      }
      setSubMenuOpen(undefined);
      closeMenu();
    };
    $[6] = anchorEl;
    $[7] = closeMenu;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleClose = t4;
  let t5;
  if ($[9] !== handleClose || $[10] !== onOptionSelected) {
    t5 = (option_0, e) => {
      if (onOptionSelected) {
        onOptionSelected(option_0);
      }
      handleClose(e);
    };
    $[9] = handleClose;
    $[10] = onOptionSelected;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSelect = t5;
  let T0;
  let T1;
  let T2;
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[12] !== disabledStates || $[13] !== handleClose || $[14] !== handleSelect || $[15] !== loadingIds || $[16] !== open || $[17] !== options || $[18] !== props || $[19] !== subMenuOpen) {
    const filteredOptions = options.filter(_temp3);
    T2 = Paper;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        boxShadow: "var(--mui-customShadows-lg)"
      };
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    T1 = ClickAwayListener;
    t9 = handleClose;
    T0 = MenuList;
    t6 = open;
    t7 = props.enablePropagation ? undefined : _temp4;
    t8 = filteredOptions.map((option_1, index) => {
      if (typeof option_1 === "string") {
        return <MenuItem key={option_1} data-testid={option_1} sx={{
          minWidth: "150px"
        }} onClick={e_1 => {
          handleSelect(option_1, e_1);
        }}>{option_1}</MenuItem>;
      } else {
        if ("divider" in option_1) {
          const previousOption = filteredOptions[index - 1];
          if (index === 0 || index === filteredOptions.length - 1 || typeof previousOption !== "string" && "divider" in previousOption) {
            return null;
          }
          if (!option_1.divider) {
            return null;
          }
          return <Divider key={option_1.id} {...option_1.dividerProps} className="my-[8px]" />;
        } else {
          if ("groupHeader" in option_1) {
            return <GroupTitle key={option_1.id} title={option_1.text} />;
          } else {
            if (option_1.nonInteractive) {
              return <Stack key={option_1.id} data-testid={option_1.id} onClick={_temp5} sx={{
                padding: "6px 16px",
                cursor: "default"
              }}><Option icon={option_1.icon} text={option_1.text} rightContent={option_1.rightContent} /></Stack>;
            } else {
              return <SubMenuInnerOption key={option_1.id} option={option_1} subMenuOpen={subMenuOpen} index={index} setSubMenuOpen={setSubMenuOpen} handleSelect={handleSelect} props={props} dynamicDisabledState={disabledStates[option_1.id]} isLoadingDisabled={loadingIds.has(option_1.id)} />;
            }
          }
        }
      }
    });
    $[12] = disabledStates;
    $[13] = handleClose;
    $[14] = handleSelect;
    $[15] = loadingIds;
    $[16] = open;
    $[17] = options;
    $[18] = props;
    $[19] = subMenuOpen;
    $[20] = T0;
    $[21] = T1;
    $[22] = T2;
    $[23] = t10;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    T0 = $[20];
    T1 = $[21];
    T2 = $[22];
    t10 = $[23];
    t6 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  let t11;
  if ($[29] !== T0 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t11 = <T0 autoFocusItem={t6} onClick={t7}>{t8}</T0>;
    $[29] = T0;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== T1 || $[35] !== t11 || $[36] !== t9) {
    t12 = <T1 onClickAway={t9}>{t11}</T1>;
    $[34] = T1;
    $[35] = t11;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== T2 || $[39] !== t10 || $[40] !== t12) {
    t13 = <T2 sx={t10}>{t12}</T2>;
    $[38] = T2;
    $[39] = t10;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const menuContent = t13;
  const t14 = !disableTransition;
  let t15;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      zIndex: 5
    };
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== disableTransition || $[44] !== menuContent) {
    t16 = disableTransition ? menuContent : t17 => {
      const {
        TransitionProps
      } = t17;
      return <Fade {...TransitionProps}>{menuContent}</Fade>;
    };
    $[43] = disableTransition;
    $[44] = menuContent;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== anchorEl || $[47] !== open || $[48] !== placement || $[49] !== popperClassName || $[50] !== t14 || $[51] !== t16) {
    t17 = <Popper open={open} anchorEl={anchorEl} placement={placement} transition={t14} disablePortal={true} className={popperClassName} sx={t15}>{t16}</Popper>;
    $[46] = anchorEl;
    $[47] = open;
    $[48] = placement;
    $[49] = popperClassName;
    $[50] = t14;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  return t17;
};
const SubMenuInnerOption = t0 => {
  const $ = _c(41);
  const {
    option,
    subMenuOpen,
    index,
    setSubMenuOpen,
    handleSelect,
    props,
    dynamicDisabledState,
    isLoadingDisabled
  } = t0;
  const [anchorEl, setAnchorEl] = useState(null);
  const isDisabled = option.menuItemProps?.disabled || isLoadingDisabled || dynamicDisabledState?.disabled;
  const tooltipTitle = isDisabled ? dynamicDisabledState?.tooltip ?? option.disabledTooltip : undefined;
  let t1;
  if ($[0] !== option.href) {
    t1 = option.href ? {
      component: Link,
      href: option.href
    } : {};
    $[0] = option.href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const linkProps = t1;
  let t2;
  if ($[2] !== index || $[3] !== option.menuItemProps || $[4] !== setSubMenuOpen) {
    t2 = e => {
      setSubMenuOpen(index);
      option.menuItemProps?.onMouseEnter?.(e);
    };
    $[2] = index;
    $[3] = option.menuItemProps;
    $[4] = setSubMenuOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== option.menuItemProps || $[7] !== setSubMenuOpen) {
    t3 = e_0 => {
      setSubMenuOpen(undefined);
      option.menuItemProps?.onMouseLeave?.(e_0);
    };
    $[6] = option.menuItemProps;
    $[7] = setSubMenuOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== handleSelect || $[10] !== option) {
    t4 = e_1 => {
      handleSelect(option, e_1);
      option.menuItemProps?.onClick?.(e_1);
    };
    $[9] = handleSelect;
    $[10] = option;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = !!option.subMenu;
  let t6;
  if ($[12] !== isLoadingDisabled || $[13] !== option.icon || $[14] !== option.rightContent || $[15] !== option.text || $[16] !== t5) {
    t6 = <Option icon={option.icon} text={option.text} rightContent={option.rightContent} isSubMenu={t5} isLoadingDisabled={isLoadingDisabled} />;
    $[12] = isLoadingDisabled;
    $[13] = option.icon;
    $[14] = option.rightContent;
    $[15] = option.text;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== anchorEl || $[19] !== index || $[20] !== option.subMenu || $[21] !== props || $[22] !== setSubMenuOpen || $[23] !== subMenuOpen) {
    t7 = option.subMenu && <InnerMenu {...props} anchorEl={anchorEl} options={option.subMenu} placement="right-start" open={subMenuOpen === index} closeMenu={() => setSubMenuOpen(undefined)} enablePropagation={true} />;
    $[18] = anchorEl;
    $[19] = index;
    $[20] = option.subMenu;
    $[21] = props;
    $[22] = setSubMenuOpen;
    $[23] = subMenuOpen;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== isDisabled || $[26] !== linkProps || $[27] !== option.id || $[28] !== option.menuItemProps || $[29] !== t2 || $[30] !== t3 || $[31] !== t4 || $[32] !== t6 || $[33] !== t7) {
    t8 = <MenuItem {...option.menuItemProps} {...linkProps} disabled={isDisabled} ref={setAnchorEl} onMouseEnter={t2} onMouseLeave={t3} onClick={t4} data-testid={option.id}>{t6}{t7}</MenuItem>;
    $[25] = isDisabled;
    $[26] = linkProps;
    $[27] = option.id;
    $[28] = option.menuItemProps;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== option.id || $[36] !== option.isAuthorized || $[37] !== option.isFeatureSupported || $[38] !== t8 || $[39] !== tooltipTitle) {
    t9 = <AuthorizationTooltip isAuthorized={option.isAuthorized} isFeatureSupported={option.isFeatureSupported} title={tooltipTitle} key={option.id}>{t8}</AuthorizationTooltip>;
    $[35] = option.id;
    $[36] = option.isAuthorized;
    $[37] = option.isFeatureSupported;
    $[38] = t8;
    $[39] = tooltipTitle;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
};
const OptionMenu = props => {
  const $ = _c(28);
  const {
    icon: t0,
    iconClassName,
    iconButtonProps,
    onToggle,
    Component,
    componentProps,
    placement: t1,
    "data-testid": t2
  } = props;
  const icon = t0 === undefined ? "material-symbols-more-horiz" : t0;
  const placement = t1 === undefined ? "bottom-end" : t1;
  const dataTestId = t2 === undefined ? "option-menu-button" : t2;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  let t3;
  if ($[0] !== onToggle || $[1] !== open) {
    t3 = event => {
      event.stopPropagation();
      setOpen(_temp6);
      onToggle?.(!open);
    };
    $[0] = onToggle;
    $[1] = open;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleToggle = t3;
  if (props.options.length === 0 || props.options.every(_temp7)) {
    return null;
  }
  const ButtonComponent = Component as React.ComponentType<Record<string, unknown> & {
    ref?: React.Ref<HTMLElement>;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  }>;
  let t4;
  if ($[3] !== ButtonComponent || $[4] !== Component || $[5] !== componentProps || $[6] !== dataTestId || $[7] !== handleToggle) {
    t4 = Component && <ButtonComponent {...componentProps} data-testid={dataTestId} ref={setAnchorEl} onClick={handleToggle} />;
    $[3] = ButtonComponent;
    $[4] = Component;
    $[5] = componentProps;
    $[6] = dataTestId;
    $[7] = handleToggle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== Component || $[10] !== dataTestId || $[11] !== handleToggle || $[12] !== icon || $[13] !== iconButtonProps || $[14] !== iconClassName) {
    t5 = !Component && <IconButton disableRipple={true} ref={setAnchorEl} data-testid={dataTestId} onClick={handleToggle} {...iconButtonProps}>{typeof icon === "string" ? <i className={classnames(icon, "text-(--mui-palette-text-primary)", iconClassName)} /> : icon}</IconButton>;
    $[9] = Component;
    $[10] = dataTestId;
    $[11] = handleToggle;
    $[12] = icon;
    $[13] = iconButtonProps;
    $[14] = iconClassName;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== onToggle) {
    t6 = () => {
      setOpen(false);
      onToggle?.(false);
    };
    $[16] = onToggle;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== anchorEl || $[19] !== open || $[20] !== placement || $[21] !== props || $[22] !== t6) {
    t7 = <InnerMenu {...props} placement={placement} open={open} closeMenu={t6} anchorEl={anchorEl} />;
    $[18] = anchorEl;
    $[19] = open;
    $[20] = placement;
    $[21] = props;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t7) {
    t8 = <>{t4}{t5}{t7}</>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
};
export const menuItemStyles = (isSelected: boolean) => {
  return {
    height: '36px',
    padding: '4px 16px 4px 16px',
    borderRadius: 0,
    ...(isSelected ? {
      backgroundColor: 'var(--mui-palette-primary-main)',
      color: 'var(--mui-palette-background-default)'
    } : {}),
    '&:hover': {
      backgroundColor: isSelected ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-primary-dark)'
    }
  };
};
export { OptionMenu };
const Option = t0 => {
  const $ = _c(17);
  const {
    icon,
    text,
    rightContent,
    isSubMenu,
    isLoadingDisabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: "4px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: "8px",
      marginRight: "8px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon) {
    t3 = (typeof icon === "string" ? <Icon iconClass={icon} color="currentColor" /> : icon) || null;
    $[2] = icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== text) {
    t4 = <Stack direction="row" sx={t2}>{t3}{text}</Stack>;
    $[4] = t3;
    $[5] = text;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isLoadingDisabled) {
    t5 = isLoadingDisabled && <Loader size="small" color="inherit" />;
    $[7] = isLoadingDisabled;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isLoadingDisabled || $[10] !== isSubMenu) {
    t6 = isSubMenu && !isLoadingDisabled && <Box component="span" sx={{
      display: "inline-flex",
      "& > span": {
        height: "20px",
        padding: "0px",
        width: "20px"
      }
    }}><Icon iconClass="material-symbols-chevron-right-rounded" color="currentColor" /></Box>;
    $[9] = isLoadingDisabled;
    $[10] = isSubMenu;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== rightContent || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Stack direction="row" sx={t1}>{t4}{rightContent}{t5}{t6}</Stack>;
    $[12] = rightContent;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
function _temp(o) {
  return typeof o === "object" && "isDisabled" in o && typeof o.isDisabled === "function";
}
function _temp2(o_0) {
  return o_0.id;
}
function _temp3(o_1) {
  return typeof o_1 === "object" && "predicate" in o_1 ? o_1.predicate?.() : true;
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp5(e_2) {
  return e_2.stopPropagation();
}
function _temp6(prevOpen) {
  return !prevOpen;
}
function _temp7(obj) {
  return typeof obj === "object" && ("predicate" in obj && !obj.predicate?.() || obj.divider);
}